import type { AppType } from '@starter/api';
import { type ClientResponse, hc } from 'hono/client';

/**
 * Typed RPC client. `AppType` is inferred from the Hono app, so a route rename
 * or a changed response shape breaks `pnpm typecheck` in CI rather than at
 * runtime in a preview.
 *
 * Same-origin by design (see apps/web/vite.config.ts), so `credentials` only
 * needs to be 'same-origin' for the session cookie to ride along.
 */
export const api = hc<AppType>('/', {
  init: { credentials: 'same-origin' },
});

export interface ApiErrorBody {
  error: string;
  requestId?: string;
}

/** The request id the API attached, useful when reporting a failure. */
export class ApiError extends Error {
  readonly status: number;
  readonly requestId: string | undefined;

  constructor(message: string, status: number, requestId?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.requestId = requestId;
  }
}

/**
 * Awaits a hono/client call and returns the decoded success body, throwing
 * ApiError on a non-2xx. Response bodies are narrowed to the success variant,
 * which is why apps/api/src/validator.ts throws instead of returning a 400.
 */
export async function unwrap<T>(request: Promise<ClientResponse<T, number, 'json'>>): Promise<T> {
  const response = await request;

  if (!response.ok) {
    let message = `${response.status} ${response.statusText}`;
    let requestId: string | undefined;
    try {
      const body = (await response.json()) as unknown as ApiErrorBody;
      if (body?.error) message = body.error;
      requestId = body?.requestId;
    } catch {
      // Non-JSON error body — keep the status line.
    }
    throw new ApiError(message, response.status, requestId);
  }

  return response.json();
}
